import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Wizard,
  BaseModal,
  useWizardFooter,
  renderWizardFooter,
  Checkbox,
  Input,
  useToastActions,
  type WizardStep,
} from '@airclou/ui';
import { dashboardApiClient } from '../../services/api';
import { listWorkspaces, Workspace } from '../../api/workspaces';

interface InviteTeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Fired after a successful invite so the open tab can refresh its list. */
  onInvited?: () => void;
}

/**
 * The "Invite member" wizard for the People page.
 *
 * It lives here rather than inside `MembersPage` because the People page's
 * Invite button sits in the page header, above the tabs, while `Tabs` renders
 * only the active tab's content. With the modal owned by the All-members tab,
 * pressing Invite from the Invited tab set state on an unmounted component and
 * nothing appeared.
 */
export function InviteTeamMemberModal({ isOpen, onClose, onInvited }: InviteTeamMemberModalProps) {
  const { t } = useTranslation('dashboard');
  const { success: toastSuccess } = useToastActions();

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [selectedWorkspaceRoles, setSelectedWorkspaceRoles] = useState<Record<string, 'admin' | 'member'>>({});
  const [inviteStep, setInviteStep] = useState<1 | 2>(1);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  // Step 2 cannot be completed without a workspace, so a failed or empty fetch
  // has to say so. Reporting "Loading workspaces..." for all three left a
  // failed fetch looking like a slow one, with Send disabled forever.
  const [workspacesState, setWorkspacesState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [error, setError] = useState<string | null>(null);

  // Workspaces are only needed once the wizard is reachable, so load on open
  // rather than on every mount of the page behind it.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setWorkspacesState('loading');
    (async () => {
      try {
        const response = await listWorkspaces();
        if (cancelled) return;
        setWorkspaces(response.workspaces ?? []);
        setWorkspacesState('ready');
      } catch (err) {
        console.error('Failed to load workspaces:', err);
        if (!cancelled) setWorkspacesState('failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const resetAndClose = () => {
    setInviteEmail('');
    setInviteName('');
    setSelectedWorkspaceRoles({});
    setInviteStep(1);
    setError(null);
    onClose();
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) {
      setError(t('people.emailRequired'));
      return;
    }

    if (Object.keys(selectedWorkspaceRoles).length === 0) {
      setError(t('people.selectAtLeastOneWorkspace'));
      return;
    }

    try {
      setInviteLoading(true);
      setError(null);

      // Build workspace_roles array from selectedWorkspaceRoles
      const workspace_roles = Object.entries(selectedWorkspaceRoles).map(([workspace_id, role]) => ({
        workspace_id,
        role
      }));

      const response = await dashboardApiClient.inviteTeamMemberWithWorkspaces(
        inviteEmail.trim(),
        inviteName.trim() || undefined,
        workspace_roles
      );

      resetAndClose();
      onInvited?.();
      // The backend answers `existing_user_added` when the person already had an
      // account and went straight into the workspaces, and
      // `new_user_created_and_added` when it created the account and mailed them
      // an invite. Those are its only two answers — the `direct_add` this used
      // to test for was never one of them — and the result went to `console.log`,
      // so a successful invite closed the modal and told the operator nothing.
      toastSuccess(
        response?.type === 'existing_user_added'
          ? t('people.teamMemberAdded')
          : t('people.invitationSent')
      );
    } catch (err: any) {
      console.error('Failed to invite member:', err);
      // The API client throws an Error carrying the envelope's `error.code` on
      // `.code` and its message on `.message`. The `err.response.data.code` this
      // replaces is an axios shape this app never produces, so the hint below
      // could not fire and every failure read as "try again" — including
      // "already a member of this team", which retrying cannot fix.
      const fromServer = err?.status || err?.code ? err?.message : null;
      if (err?.code === 'USER_NOT_FOUND') {
        setError(t('people.userNotFoundHint'));
      } else {
        setError(fromServer || t('people.failedToInvite'));
      }
    } finally {
      setInviteLoading(false);
    }
  };

  const wizardSteps = useMemo<WizardStep[]>(() => [
    {
      id: 'member-info',
      title: t('people.memberInfo'),
      content: (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('people.emailAddressLabel')} <span className="text-red-500">*</span>
            </label>
            <Input
              type="email"
              value={inviteEmail}
              onChange={setInviteEmail}
              placeholder={t('people.enterEmailAddress')}
              disabled={inviteLoading}
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('people.nameOptional')}
            </label>
            <Input
              type="text"
              value={inviteName}
              onChange={setInviteName}
              placeholder={t('people.enterName')}
              disabled={inviteLoading}
            />
          </div>

          <div className="pt-2">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('people.nextStepHelp')}
            </p>
          </div>
        </div>
      ),
      validate: () => inviteEmail.trim().length > 0
    },
    {
      id: 'select-workspaces',
      title: t('people.selectWorkspaces'),
      content: (
        <div className="space-y-4">
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="text-sm">
              <span className="font-medium text-foreground">{t('people.inviting')}</span>{' '}
              <span className="text-gray-700 dark:text-gray-300">{inviteEmail}</span>
              {inviteName && <>  ({inviteName})</>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              {t('people.addToWorkspaces')} <span className="text-red-500">*</span>
            </label>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              {t('people.workspaceAccessHelp')}
            </p>
            <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-3 bg-gray-50 dark:bg-gray-900/20">
              {workspacesState === 'loading' ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">{t('people.loadingWorkspaces')}</div>
              ) : workspacesState === 'failed' ? (
                <div className="text-sm text-red-600 dark:text-red-400">{t('people.failedToLoadWorkspaces')}</div>
              ) : workspaces.length === 0 ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">{t('people.noWorkspacesYet')}</div>
              ) : (
                workspaces.map((workspace) => {
                  const isSelected = workspace.id in selectedWorkspaceRoles;
                  const currentRole = selectedWorkspaceRoles[workspace.id] || 'member';

                  return (
                    <div
                      key={workspace.id}
                      className="flex items-center justify-between gap-3 p-3 rounded-lg hover:bg-white dark:hover:bg-gray-800 transition-colors border border-transparent hover:border-gray-200 dark:hover:border-gray-700"
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedWorkspaceRoles({ ...selectedWorkspaceRoles, [workspace.id]: 'member' });
                          } else {
                            const newRoles = { ...selectedWorkspaceRoles };
                            delete newRoles[workspace.id];
                            setSelectedWorkspaceRoles(newRoles);
                          }
                        }}
                        label={
                          <div className="flex flex-col">
                            <span>{workspace.name}</span>
                            {workspace.company_name && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {workspace.company_name}
                              </span>
                            )}
                          </div>
                        }
                        disabled={inviteLoading}
                      />
                      {isSelected && (
                        <select
                          value={currentRole}
                          onChange={(e) => {
                            setSelectedWorkspaceRoles({
                              ...selectedWorkspaceRoles,
                              [workspace.id]: e.target.value as 'admin' | 'member'
                            });
                          }}
                          className="text-sm rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 focus:ring-primary focus:border-primary"
                          disabled={inviteLoading}
                        >
                          <option value="member">{t('common.member')}</option>
                          <option value="admin">{t('common.admin')}</option>
                        </select>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {Object.keys(selectedWorkspaceRoles).length === 0 ? (
                <span className="text-amber-600 dark:text-amber-400">
                  {t('people.pleaseSelectWorkspace')}
                </span>
              ) : (
                <span className="text-green-600 dark:text-green-400">
                  {t('people.workspacesSelected', { count: Object.keys(selectedWorkspaceRoles).length, plural: Object.keys(selectedWorkspaceRoles).length === 1 ? '' : 's' })}
                </span>
              )}
            </div>
          </div>
        </div>
      ),
      validate: () => Object.keys(selectedWorkspaceRoles).length > 0
    }
  ], [inviteEmail, inviteName, workspaces, workspacesState, selectedWorkspaceRoles, inviteLoading, t]);

  const wizardFooterProps = useWizardFooter({
    steps: wizardSteps,
    currentStep: inviteStep,
    onStepChange: (step) => setInviteStep(step as 1 | 2),
    onComplete: handleInviteMember,
    onCancel: resetAndClose,
    isLoading: inviteLoading,
    canGoBack: true,
    nextButtonText: t('people.nextSelectWorkspaces'),
    completeButtonText: t('people.sendInvite')
  });

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={resetAndClose}
      title={t('people.inviteTeamMember')}
      size="lg"
      footer={renderWizardFooter(wizardFooterProps)}
    >
      <Wizard
        steps={wizardSteps}
        currentStep={inviteStep}
        onStepChange={(step) => setInviteStep(step as 1 | 2)}
        onComplete={handleInviteMember}
        onCancel={resetAndClose}
        isLoading={inviteLoading}
        showStepIndicator={true}
        showStepTitles={true}
        errorMessage={error}
        externalFooter={true}
      />
    </BaseModal>
  );
}
